import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import PageHeader from "@/components/layout/PageHeader";
import { contactService, Contact } from "@/services/contactService";
import {
  ContactDetailsSidebar,
  ContactDetailsData,
} from "@/components/ContactDetailsSidebar";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Filter,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
} from "lucide-react";
import {
  ConversationStatus,
  defaultVisibleStatuses,
} from "@/types/conversation";
import { StatusFilter } from "@/components/ui/status-filter";
import { StatusDropdown } from "@/components/ui/status-dropdown";
import { format, formatDistanceToNow } from "date-fns";
import {
  SkeletonTable,
  Skeleton,
  SkeletonText,
} from "@/components/ui/skeleton";
import { useContacts } from "@/contexts/ContactsContext";

// Helper function to generate avatar background colors
const getAvatarColor = (name: string) => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
  ];

  if (!name) return colors[0];

  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

// Helper function to get initials
const getInitials = (name: string) => {
  if (!name || name === "Anonymous") return "A";

  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Helper function to format relative dates
const formatRelativeDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (dateOnly.getTime() === today.getTime()) {
    return "Today";
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    return "Yesterday";
  } else if (date.getFullYear() === now.getFullYear()) {
    // Same year, show month and day
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } else {
    // Different year, show full date
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
};

// Helper function to get priority badge variant and styling
const getPriorityVariant = (priority: string | null | undefined) => {
  switch (priority) {
    case "urgent":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    case "high":
      return {
        className:
          "capitalize text-xs bg-orange-100 text-orange-800 border-orange-200",
        variant: "outline" as const,
      };
    case "medium":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200",
        variant: "outline" as const,
      };
    case "low":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

// Helper function to get sentiment badge variant and styling
const getSentimentVariant = (sentiment: string | null | undefined) => {
  switch (sentiment) {
    case "positive":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    case "neutral":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "negative":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

// Helper function to get user type badge variant and styling
const getUserTypeBadgeProps = (userType: string | null | undefined) => {
  switch (userType) {
    case "lead":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
        variant: "outline" as const,
      };
    case "client":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-700",
        variant: "outline" as const,
      };
  }
};

// Helper function to format field values for display
function formatFieldValue(value: any): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "object") {
    // Extract meaningful value from objects
    if (value.value !== undefined) return String(value.value);
    if (value.name !== undefined) return String(value.name);
    // Fallback to readable object representation
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");
  }

  return String(value);
}

type SortField =
  | "date"
  | "type"
  | "intent"
  | "sentiment"
  | "priority"
  | "status";
type SortDirection = "asc" | "desc" | null;

export default function Contacts() {
  const navigate = useNavigate();

  // Use ContactsContext instead of local state
  const {
    isLoading,
    error,
    selectedContactId,
    searchTerm,
    userTypeFilter,
    sentimentFilter,
    showHighPriority,
    statusFilter,
    sortField,
    sortDirection,
    setSearchTerm,
    setUserTypeFilter,
    setSentimentFilter,
    setShowHighPriority,
    setStatusFilter,
    setSortField,
    setSortDirection,
    updateContact,
    getFilteredContacts,
    refreshContacts,
    selectContact,
    getContactFormFields,
  } = useContacts();

  // Local sidebar state only
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedContact, setSelectedContact] =
    useState<ContactDetailsData | null>(null);

  // Handle tab change to navigate between inbox and contacts
  const handleTabChange = (value: string) => {
    if (value === "inbox") {
      navigate("/inbox");
    }
  };

  // Manual refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { toast } = useToast();

  // Handle any context errors
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load contacts. Please try again.",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Manual refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshContacts();
      toast({
        title: "Contacts refreshed",
        description: "Contact data has been updated successfully.",
      });
    } catch (error) {
      console.error("Error refreshing contacts:", error);
      toast({
        title: "Refresh failed",
        description: "Failed to refresh contacts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field)
      return <ArrowUpDown className="h-4 w-4" style={{ color: "#667085" }} />;
    if (sortDirection === "asc")
      return <ArrowUp className="h-4 w-4" style={{ color: "#667085" }} />;
    if (sortDirection === "desc")
      return <ArrowDown className="h-4 w-4" style={{ color: "#667085" }} />;
    return <ArrowUpDown className="h-4 w-4" style={{ color: "#667085" }} />;
  };

  // Get filtered contacts from context
  const filteredAndSortedContacts = getFilteredContacts();

  // Handle contact selection for sidebar - simplified to use contact data directly
  const handleContactClick = (contact: Contact) => {
    console.log("Contact clicked:", contact.name, contact.id);

    // No loading state needed - we use the contact data directly
    setSidebarOpen(true);

    // Map contact data directly to sidebar format
    const contactDetailsData: ContactDetailsData = {
      id: contact.id,
      user_name: contact.name || "Anonymous",
      user_email: contact.email,
      user_phone: contact.user_phone,
      user_type: contact.user_type,
      priority: contact.priority,
      sentiment: contact.sentiment,
      intent: contact.intent,
      next_step: contact.next_step,
      summary: null, // Not available in contact data
      tags: contact.tags,
      estimated_time_saved: contact.estimated_time_saved,
      message_count: contact.message_count || 0,
      form_fields: undefined, // Form fields can be loaded separately if needed
    };

    console.log("Setting contact data:", contactDetailsData);
    setSelectedContact(contactDetailsData);
  };

  const handleCloseSidebar = () => {
    console.log("Closing sidebar");
    setSidebarOpen(false);
    setSelectedContact(null);
  };

  if (isLoading) {
    return (
      <MainLayout>
        {/* Fixed PageHeader */}
        <div className="border-b bg-white">
          <div className="px-6 py-4">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold">Chats</h1>
              <Tabs
                value="list"
                onValueChange={handleTabChange}
                className="w-64"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="inbox">Inbox</TabsTrigger>
                  <TabsTrigger value="list">List View</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
        <div className="flex flex-col h-full">
          {/* Filters Section Skeleton */}
          <div className="flex-shrink-0 p-6 pb-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
                <Skeleton className="h-10 w-full sm:w-64" />
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-28" />
                <Skeleton className="h-10 w-36" />
              </div>
              <SkeletonText className="h-5 w-24" />
            </div>
          </div>

          {/* Contacts Table Skeleton */}
          <div className="flex-1 p-6 pt-6">
            <SkeletonTable
              rows={8}
              columns={8}
              showHeader={true}
              showAvatar={true}
              avatarColumn={0}
              className="h-full"
            />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Fixed PageHeader */}
      <div className="border-b bg-white">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold">Chats</h1>
            <Tabs value="list" onValueChange={handleTabChange} className="w-64">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="inbox">Inbox</TabsTrigger>
                <TabsTrigger value="list">List View</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Filters Section - Fixed at top */}
        <div className="flex-shrink-0 p-6 pb-0 bg-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Filters and Search - Left side on desktop, stacked on mobile */}
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
              {/* Search */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="By name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-white border-gray-300"
                />
              </div>

              {/* User Type Tabs */}
              <Tabs
                value={userTypeFilter}
                onValueChange={(value) =>
                  setUserTypeFilter(value as "all" | "leads" | "clients")
                }
              >
                <TabsList className="bg-gray-100">
                  <TabsTrigger value="all" className="text-sm">
                    All
                  </TabsTrigger>
                  <TabsTrigger value="leads" className="text-sm">
                    Leads
                  </TabsTrigger>
                  <TabsTrigger value="clients" className="text-sm">
                    Clients
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Status Filter */}
              <StatusFilter
                selectedStatuses={statusFilter}
                onStatusChange={setStatusFilter}
              />

              {/* High Priority Filter */}
              <Button
                variant={showHighPriority ? "default" : "outline"}
                size="sm"
                onClick={() => setShowHighPriority(!showHighPriority)}
                className="flex items-center gap-2 border-gray-300 whitespace-nowrap"
              >
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden sm:inline">High Priority</span>
                <span className="sm:hidden">Priority</span>
              </Button>

              {/* Sentiment Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <Select
                  value={sentimentFilter}
                  onValueChange={setSentimentFilter}
                >
                  <SelectTrigger className="w-[140px] bg-white border-gray-300">
                    <SelectValue placeholder="Sentiment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sentiment</SelectItem>
                    <SelectItem value="positive">Positive</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="negative">Negative</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Results Count and Refresh Button - Right side on desktop, below on mobile */}
            <div className="flex items-center justify-end lg:justify-start gap-3">
              <p className="text-sm text-gray-600">
                {filteredAndSortedContacts.length} contacts
              </p>

              {/* Manual Refresh Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing || isLoading}
                className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Refresh contacts"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div className="flex-1 p-6 pt-6 overflow-hidden">
          <div className="bg-white rounded-lg h-full flex flex-col">
            {/* Scrollable Table with Fixed Header */}
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-gray-50 z-10">
                  <TableRow>
                    <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] py-4">
                      Name
                    </TableHead>
                    <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("date")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Date
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("date")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px] text-xs font-medium text-[#667085]">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("type")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Type
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("type")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px] text-xs font-medium text-[#667085]">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("priority")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Priority
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("priority")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("intent")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Intent
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("intent")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[176px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("status")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Status
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("status")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                      Next Steps
                    </TableHead>
                    <TableHead className="min-w-[100px] text-xs font-medium text-[#667085] hidden md:table-cell">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("sentiment")}
                        className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                      >
                        Sentiment
                        <span className="ml-1 text-[#667085]">
                          {getSortIcon("sentiment")}
                        </span>
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[400px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                      Tags
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedContacts.map((contact, index) => (
                    <TableRow
                      key={`${contact.id}-${contact.conversation_id}-${index}`}
                      className="hover:bg-gray-50 border-b border-gray-200"
                    >
                      {/* Name Column without Avatar and without phone */}
                      <TableCell className="py-4">
                        <div className="flex flex-col">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleContactClick(contact);
                            }}
                            className="font-medium text-gray-900 hover:underline hover:text-blue-600 transition-colors text-left"
                            style={{ fontWeight: 500 }}
                          >
                            {contact.name || "Anonymous"}
                          </button>
                          <div className="text-sm text-gray-500">
                            <div className="truncate">{contact.email}</div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Date Column */}
                      <TableCell className="py-4">
                        <div className="text-sm text-gray-600">
                          {formatRelativeDate(contact.conversation_date)}
                        </div>
                      </TableCell>

                      {/* Type Column */}
                      <TableCell className="py-4">
                        <Badge
                          variant={
                            getUserTypeBadgeProps(contact.user_type).variant
                          }
                          className={
                            getUserTypeBadgeProps(contact.user_type).className
                          }
                        >
                          {contact.user_type || "Unknown"}
                        </Badge>
                      </TableCell>

                      {/* Priority Column */}
                      <TableCell className="py-4">
                        {contact.priority ? (
                          <Badge
                            variant={
                              getPriorityVariant(contact.priority).variant
                            }
                            className={
                              getPriorityVariant(contact.priority).className
                            }
                          >
                            {contact.priority}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* Intent Column - Hidden on mobile */}
                      <TableCell className="py-4 hidden lg:table-cell">
                        <div
                          className="text-sm text-gray-700 max-w-[200px] truncate"
                          title={contact.intent || ""}
                        >
                          {contact.intent || (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Status Column - Hidden on mobile */}
                      <TableCell className="py-4 hidden lg:table-cell">
                        <StatusDropdown
                          conversationId={contact.conversation_id}
                          currentStatus={contact.status}
                          onStatusChange={(newStatus) => {
                            // Update the contact using context
                            updateContact(contact.conversation_id, {
                              status: newStatus,
                            });
                          }}
                          size="sm"
                        />
                      </TableCell>

                      {/* Next Steps Column - Hidden on mobile */}
                      <TableCell className="py-4 hidden lg:table-cell">
                        <div
                          className="text-sm text-gray-700 max-w-[200px] truncate"
                          title={contact.next_step || ""}
                        >
                          {contact.next_step || (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Sentiment Column - Hidden on small screens */}
                      <TableCell className="py-4 hidden md:table-cell">
                        {contact.sentiment ? (
                          <Badge
                            variant={
                              getSentimentVariant(contact.sentiment).variant
                            }
                            className={
                              getSentimentVariant(contact.sentiment).className
                            }
                          >
                            {contact.sentiment}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* Tags Column - Hidden on mobile */}
                      <TableCell className="py-4 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {contact.tags && contact.tags.length > 0 ? (
                            contact.tags.slice(0, 2).map((tag, index) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="text-xs bg-gray-100 text-gray-700 border-0"
                              >
                                {tag}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                          {contact.tags && contact.tags.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-xs border-gray-300 text-gray-600"
                            >
                              +{contact.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}

                  {filteredAndSortedContacts.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <p className="text-gray-500 text-lg">
                          No contacts found
                        </p>
                        <p className="text-gray-400 text-sm mt-2">
                          Try adjusting your search or filter criteria
                        </p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Modal Sidebar */}
        <ContactDetailsSidebar
          isOpen={sidebarOpen}
          onClose={handleCloseSidebar}
          contactData={selectedContact}
          isLoading={false} // loadingContactDetails is removed
          title="Contact Details"
        />
      </div>
    </MainLayout>
  );
}
